import type { OrgKey } from '@compass/client/types/org_key';
import { useIsSystemAdmin } from '@hooks/useIsSystemAdmin';
import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { useOrgKeys } from '@hooks/useOrgKeys';
import { useMyRoles } from '@hooks/useUsers';
import { getErrorMessage } from '@utils/errorMessages';
import { AlertCircle, KeyRound, MoreHorizontal, Plus } from 'lucide-react';
import React, { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useStore } from '@/store';

import { CreateOrgKeyDialog } from './CreateOrgKeyDialog';
import { DeleteOrgKeyDialog } from './DeleteOrgKeyDialog';
import { canManageOrgKeys, orgKeyStatusLabel } from './orgKeys';
import { RotateOrgKeyDialog } from './RotateOrgKeyDialog';
import { formatDate, formatRelative } from './settingsFormatting';
import { ShowOrgKeyDialog } from './ShowOrgKeyDialog';

type RowDialog = 'rotate' | 'delete';

export function OrgKeysSection() {
  const { organizations } = useOrgAndPrograms();
  const { data: rolesData } = useMyRoles();
  const isSystemAdmin = useIsSystemAdmin();
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const canManage = canManageOrgKeys(
    rolesData?.user_roles ?? [],
    isSystemAdmin,
    selectedOrganizationId,
  );
  const organizationId = canManage ? selectedOrganizationId : null;
  const organizationName =
    organizations.find(org => org.id === organizationId)?.name || organizationId || '';

  const { data, isLoading, isError, error } = useOrgKeys(organizationId);
  const orgKeys = data?.org_keys ?? [];

  const [createOpen, setCreateOpen] = useState(false);
  const [newApiKey, setNewApiKey] = useState<string | null>(null);
  const [activeRow, setActiveRow] = useState<{ key: OrgKey; dialog: RowDialog } | null>(null);

  if (!organizationId) {
    return null;
  }

  return (
    <>
      <div className="mt-6 rounded-lg border bg-card p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-muted">
              <KeyRound className="h-6 w-6 text-foreground" />
            </div>
            <div>
              <h3 className="text-sm font-semibold">API Keys</h3>
              <p className="text-sm text-muted-foreground">
                Let your organization&apos;s integrations call the Compass API. Each key is shown
                only once.
              </p>
            </div>
          </div>
          <Button size="sm" className="shrink-0" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New
          </Button>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : isError ? (
            <div className="bg-destructive/10 border border-destructive/50 text-destructive px-4 py-3 rounded-md text-sm">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">Failed to load API keys</p>
                  <p className="mt-1">
                    {getErrorMessage(error ?? {}) || 'Please try again later.'}
                  </p>
                </div>
              </div>
            </div>
          ) : orgKeys.length === 0 ? (
            <div className="rounded-md border border-dashed bg-muted/30 px-6 py-8 text-center">
              <p className="text-sm font-medium">No API keys yet</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Create one to authenticate your organization&apos;s integrations against the Compass
                API.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Create API key
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Mode</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead>Last used</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orgKeys.map(key => {
                    const status = orgKeyStatusLabel(key);
                    return (
                      <TableRow key={key.org_key_id}>
                        <TableCell className="font-medium">{key.name || '—'}</TableCell>
                        <TableCell>
                          <Badge variant={key.is_sandbox ? 'secondary' : 'default'}>
                            {key.is_sandbox ? 'Developer' : 'Live'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(key.created_at)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {key.expires_at ? formatDate(key.expires_at) : 'Never'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatRelative(key.last_used_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={status === 'Expired' ? 'destructive' : 'success'}>
                            {status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Actions</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onSelect={() => setActiveRow({ key, dialog: 'rotate' })}
                              >
                                Rotate
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onSelect={() => setActiveRow({ key, dialog: 'delete' })}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <CreateOrgKeyDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        organizationId={organizationId}
        organizationName={organizationName}
        onCreated={apiKey => setNewApiKey(apiKey)}
      />

      {newApiKey && (
        <ShowOrgKeyDialog
          open={newApiKey !== null}
          onOpenChange={next => {
            if (!next) setNewApiKey(null);
          }}
          apiKey={newApiKey}
        />
      )}

      {activeRow?.dialog === 'rotate' && (
        <RotateOrgKeyDialog
          open
          onOpenChange={next => {
            if (!next) setActiveRow(null);
          }}
          orgKey={activeRow.key}
          onRotated={apiKey => {
            setActiveRow(null);
            setNewApiKey(apiKey);
          }}
        />
      )}

      {activeRow?.dialog === 'delete' && (
        <DeleteOrgKeyDialog
          open
          onOpenChange={next => {
            if (!next) setActiveRow(null);
          }}
          orgKey={activeRow.key}
        />
      )}
    </>
  );
}
